import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Buildings, MapPin, QrCode, Printer, DownloadSimple, Copy } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import { downloadQrKaart, copyQrKaart } from "@/lib/qrExport";
import { useAuth, usePermissions } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FRONTEND_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;

const emptyForm = { naam: "", adres: "", stad: "", telefoon: "", weekrooster: {} };

const DAGEN = [
  { k: "ma", l: "Maandag" },
  { k: "di", l: "Dinsdag" },
  { k: "wo", l: "Woensdag" },
  { k: "do", l: "Donderdag" },
  { k: "vr", l: "Vrijdag" },
  { k: "za", l: "Zaterdag" },
  { k: "zo", l: "Zondag" },
];

export default function Filialen({ filialen, medewerkers, onRefresh, loading }) {
  const user = useAuth();
  const perms = usePermissions();
  const role = user?.role || "medewerker";
  const canEdit = perms[role]?.filialen_bewerken === true;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrFiliaal, setQrFiliaal] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const openQr = (f) => {
    setQrFiliaal(f);
    setQrDialogOpen(true);
  };

  // Count medewerkers per filiaal
  const countPerFiliaal = {};
  filialen.forEach((f) => (countPerFiliaal[f.id] = 0));
  medewerkers.forEach((m) => {
    (m.filiaal_ids || []).forEach((fid) => {
      if (countPerFiliaal[fid] !== undefined) countPerFiliaal[fid]++;
    });
  });

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (f) => {
    setEditing(f);
    setForm({
      naam: f.naam,
      adres: f.adres || "",
      stad: f.stad || "",
      telefoon: f.telefoon || "",
      weekrooster: f.weekrooster || {},
    });
    setDialogOpen(true);
  };

  const openDelete = (f) => {
    setDeleting(f);
    setDeleteDialogOpen(true);
  };

  // Rooster kopiëren
  const kopieerRoosterVan = (fid) => {
    const bron = filialen.find((f) => f.id === fid);
    if (!bron) return;
    setForm((f) => ({ ...f, weekrooster: JSON.parse(JSON.stringify(bron.weekrooster || {})) }));
    toast.success(`Rooster overgenomen van ${bron.naam}`);
  };

  const kopieerDagNaarAlle = (k) => {
    setForm((f) => {
      const rij = (f.weekrooster || {})[k] || {};
      const nieuw = {};
      DAGEN.forEach((d) => (nieuw[d.k] = { ...rij }));
      return { ...f, weekrooster: nieuw };
    });
    toast.success("Gekopieerd naar alle dagen");
  };

  const handleSave = async () => {
    if (!form.naam.trim()) {
      toast.error("Naam is verplicht");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/filialen/${editing.id}`, form);
        toast.success("Filiaal bijgewerkt");
      } else {
        await axios.post(`${API}/filialen`, form);
        toast.success("Filiaal toegevoegd");
      }
      setDialogOpen(false);
      onRefresh();
    } catch (e) {
      toast.error("Fout bij opslaan");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await axios.delete(`${API}/filialen/${deleting.id}`);
      toast.success("Filiaal verwijderd");
      setDeleteDialogOpen(false);
      onRefresh();
    } catch (e) {
      toast.error("Fout bij verwijderen");
    }
  };

  if (loading) {
    return (
      <div data-testid="filialen-loading" className="space-y-4">
        <div className="h-8 w-48 bg-muted animate-pulse rounded-sm" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-40 bg-muted animate-pulse rounded-sm" />
          ))}
        </div>
      </div>
    );
  }


  const printFiliaalQr = () => {
    const svg = document.getElementById("filiaal-qr-svg")?.querySelector("svg");
    const url = qrFiliaal ? `${FRONTEND_URL}/inklokken/${qrFiliaal.id}` : "";
    const html = `<!DOCTYPE html><html lang="nl"><head><meta charset="utf-8"/><title>QR ${qrFiliaal?.naam || ""}</title>
      <style>
        @page { margin: 15mm; }
        body{margin:0;padding:20px;font-family:Arial,Helvetica,sans-serif;color:#111;text-align:center}
        .card{border:3px solid #111;border-radius:4px;padding:36px 28px;max-width:360px;margin:0 auto}
        .naam{font-size:22px;font-weight:900;margin-bottom:4px}
        .sub{font-size:11px;color:#555;margin-bottom:18px}
        .url{font-size:8px;font-family:monospace;color:#999;word-break:break-all;margin-top:14px}
        svg{display:inline-block}
      </style></head><body><div class="card">
        <div class="naam">${qrFiliaal?.naam || ""}</div>
        <div class="sub">Scan om in te klokken</div>
        ${svg?.outerHTML || ""}
        <div class="url">${url}</div>
      </div><script>window.onload=()=>window.print();<\/script></body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Sta pop-ups toe om te printen"); return; }
    w.document.write(html);
    w.document.close();
  };

  return (
    <div data-testid="filialen-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Filialen</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {filialen.length} filia{filialen.length !== 1 ? "len" : "al"} geregistreerd
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={openNew}
            className="rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white"
            data-testid="add-filiaal-btn"
          >
            <Plus size={18} weight="bold" className="mr-2" />
            Nieuw Filiaal
          </Button>
        )}
      </div>

      {filialen.length === 0 ? (
        <div
          className="bg-white border border-border rounded-sm p-12 text-center text-muted-foreground"
          data-testid="no-filialen-found"
        >
          <Buildings size={48} className="mx-auto mb-4 opacity-40" />
          <p className="text-sm">Nog geen filialen. Klik op 'Nieuw Filiaal' om te beginnen.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="filialen-grid">
          {filialen.map((f) => {
            const count = countPerFiliaal[f.id] || 0;

            return (
              <div
                key={f.id}
                className="bg-white border border-border rounded-sm p-5 hover:-translate-y-0.5 transition-all duration-200 group"
                data-testid={`filiaal-card-${f.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                      <Buildings size={22} weight="bold" className="text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold text-base">{f.naam}</h3>
                      {f.stad && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                          <MapPin size={12} />
                          {f.stad}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => openQr(f)}
                      className="p-1.5 rounded-sm hover:bg-primary/10 transition-colors text-primary"
                      data-testid={`qr-filiaal-${f.id}`}
                      title="QR Code"
                    >
                      <QrCode size={16} />
                    </button>
                    {canEdit && (
                      <>
                        <button onClick={() => openEdit(f)} className="p-1.5 rounded-sm hover:bg-muted transition-colors" data-testid={`edit-filiaal-${f.id}`}><PencilSimple size={16} /></button>
                        <button onClick={() => openDelete(f)} className="p-1.5 rounded-sm hover:bg-destructive/10 text-destructive transition-colors" data-testid={`delete-filiaal-${f.id}`}><Trash size={16} /></button>
                      </>
                    )}
                  </div>
                </div>

                {f.adres && (
                  <p className="text-sm text-muted-foreground mb-3">{f.adres}</p>
                )}

                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <Badge variant="secondary" className="text-xs">
                    {count} medewerker{count !== 1 ? "s" : ""}
                  </Badge>
                </div>

                {f.telefoon && (
                  <p className="text-xs text-muted-foreground mt-2">Tel: {f.telefoon}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-sm" data-testid="filiaal-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              {editing ? "Filiaal Bewerken" : "Nieuw Filiaal"}
            </DialogTitle>
            <DialogDescription>
              {editing ? "Pas de gegevens van dit filiaal aan." : "Vul de gegevens in voor het nieuwe filiaal."}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <div>
              <Label htmlFor="naam" className="text-xs font-semibold">Naam *</Label>
              <Input
                id="naam"
                value={form.naam}
                onChange={(e) => setForm({ ...form, naam: e.target.value })}
                placeholder="Filiaal Amsterdam"
                className="rounded-sm mt-1"
                data-testid="input-filiaal-naam"
              />
            </div>
            <div>
              <Label htmlFor="adres" className="text-xs font-semibold">Adres</Label>
              <Input
                id="adres"
                value={form.adres}
                onChange={(e) => setForm({ ...form, adres: e.target.value })}
                placeholder="Keizersgracht 123"
                className="rounded-sm mt-1"
                data-testid="input-filiaal-adres"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="stad" className="text-xs font-semibold">Stad</Label>
                <Input
                  id="stad"
                  value={form.stad}
                  onChange={(e) => setForm({ ...form, stad: e.target.value })}
                  placeholder="Amsterdam"
                  className="rounded-sm mt-1"
                  data-testid="input-filiaal-stad"
                />
              </div>
              <div>
                <Label htmlFor="tel" className="text-xs font-semibold">Telefoon</Label>
                <Input
                  id="tel"
                  value={form.telefoon}
                  onChange={(e) => setForm({ ...form, telefoon: e.target.value })}
                  placeholder="020-1234567"
                  className="rounded-sm mt-1"
                  data-testid="input-filiaal-telefoon"
                />
              </div>
            </div>

            <div className="border border-border rounded-sm p-3">
              <Label className="text-xs font-bold uppercase tracking-wider text-[#002FA7]">
                Openings- en sluitingsuren
              </Label>
              <p className="text-[10px] text-muted-foreground mt-0.5 mb-2">
                Enkel voor dit filiaal. Laat een dag leeg om de firma-uren uit Instellingen te gebruiken.
                Het sluitingsuur bepaalt wanneer de herinnering "vergeten uitklokken" komt.
              </p>
              {filialen.filter((f) => f.id !== editing?.id && Object.keys(f.weekrooster || {}).length)
                .length > 0 && (
                <div className="flex items-center gap-2 mb-2 bg-[#EAF1FF] border border-[#002FA7]/20 rounded-sm p-2">
                  <Copy size={14} weight="bold" className="text-[#002FA7] shrink-0" />
                  <span className="text-[11px] font-semibold shrink-0">Overnemen van</span>
                  <Select onValueChange={kopieerRoosterVan}>
                    <SelectTrigger className="h-8 rounded-sm text-xs" data-testid="fil-rooster-copy-from">
                      <SelectValue placeholder="Kies filiaal..." />
                    </SelectTrigger>
                    <SelectContent>
                      {filialen
                        .filter((f) => f.id !== editing?.id && Object.keys(f.weekrooster || {}).length)
                        .map((f) => (
                          <SelectItem key={f.id} value={f.id}>{f.naam}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1.5">
                {DAGEN.map((d) => {
                  const rij = (form.weekrooster || {})[d.k] || {};
                  const zet = (veld, waarde) =>
                    setForm((f) => ({
                      ...f,
                      weekrooster: { ...(f.weekrooster || {}), [d.k]: { ...rij, [veld]: waarde } },
                    }));
                  return (
                    <div key={d.k} className="flex items-center gap-2" data-testid={`fil-rooster-${d.k}`}>
                      <span className="w-[72px] text-xs font-bold shrink-0">{d.l}</span>
                      <Input
                        type="time"
                        value={rij.start || ""}
                        onChange={(e) => zet("start", e.target.value)}
                        disabled={rij.gesloten}
                        className="rounded-sm h-9 w-[104px]"
                        data-testid={`fil-rooster-${d.k}-start`}
                      />
                      <span className="text-xs text-muted-foreground">tot</span>
                      <Input
                        type="time"
                        value={rij.eind || ""}
                        onChange={(e) => zet("eind", e.target.value)}
                        disabled={rij.gesloten}
                        className="rounded-sm h-9 w-[104px]"
                        data-testid={`fil-rooster-${d.k}-eind`}
                      />
                      <button
                        type="button"
                        onClick={() => kopieerDagNaarAlle(d.k)}
                        title={`Kopieer ${d.l} naar alle dagen`}
                        className="w-8 h-8 flex items-center justify-center text-[#002FA7] border border-[#002FA7]/30 rounded-sm active:bg-[#EAF1FF] shrink-0"
                        data-testid={`fil-rooster-${d.k}-copy-all`}
                      >
                        <Copy size={13} weight="bold" />
                      </button>
                      <label className="flex items-center gap-1.5 ml-2 shrink-0">
                        <Switch
                          checked={!!rij.gesloten}
                          onCheckedChange={(v) => zet("gesloten", v)}
                          data-testid={`fil-rooster-${d.k}-gesloten`}
                        />
                        <span className="text-[11px] font-semibold text-muted-foreground">Gesloten</span>
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="rounded-sm"
              data-testid="cancel-filiaal-btn"
            >
              Annuleren
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white"
              data-testid="save-filiaal-btn"
            >
              {saving ? "Opslaan..." : editing ? "Bijwerken" : "Toevoegen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md rounded-sm" data-testid="delete-filiaal-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              Filiaal Verwijderen
            </DialogTitle>
            <DialogDescription>
              Weet u zeker dat u <strong>{deleting?.naam}</strong> wilt verwijderen?
              Medewerkers die aan dit filiaal zijn gekoppeld worden niet verwijderd.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="rounded-sm"
              data-testid="cancel-delete-filiaal-btn"
            >
              Annuleren
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              className="rounded-sm"
              data-testid="confirm-delete-filiaal-btn"
            >
              Verwijderen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="sm:max-w-md rounded-sm" data-testid="qr-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              QR Code — {qrFiliaal?.naam}
            </DialogTitle>
            <DialogDescription>
              Medewerkers scannen deze QR code om in te klokken bij dit filiaal.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center py-6">
            {qrFiliaal && (
              <div id="filiaal-qr-svg" className="bg-white p-4 border border-border rounded-sm">
                <QRCodeSVG
                  value={`${FRONTEND_URL}/inklokken/${qrFiliaal.id}`}
                  size={220}
                  level="H"
                  data-testid="qr-code-svg"
                />
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-4 text-center max-w-[280px]">
              Print deze QR code en hang hem op bij het filiaal.
              Medewerkers scannen met hun telefoon om in te klokken.
            </p>
            <div className="mt-3 text-xs bg-muted rounded-sm px-3 py-1.5 font-mono break-all text-center" data-testid="qr-url">
              {qrFiliaal && `${FRONTEND_URL}/inklokken/${qrFiliaal.id}`}
            </div>
          </div>
          <DialogFooter className="flex-wrap gap-2 sm:justify-center">
            <Button
              onClick={printFiliaalQr}
              className="rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white"
              data-testid="print-filiaal-qr-btn"
            >
              <Printer size={16} weight="bold" className="mr-1.5" /> Print
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                downloadQrKaart("filiaal-qr-svg", `qr-${(qrFiliaal?.naam || "filiaal").replace(/\s+/g, "-")}`, {
                  titel: qrFiliaal?.naam || "",
                  subtitel: [qrFiliaal?.adres, qrFiliaal?.stad].filter(Boolean).join(" - "),
                  regels: ["Scan om in te klokken"],
                }).catch(() => toast.error("PNG maken mislukt"))
              }
              className="rounded-sm"
              data-testid="filiaal-qr-png-btn"
            >
              <DownloadSimple size={16} weight="bold" className="mr-1.5" /> PNG
            </Button>
            <Button
              variant="outline"
              onClick={async () => {
                const ok = await copyQrKaart("filiaal-qr-svg", {
                  titel: qrFiliaal?.naam || "",
                  subtitel: [qrFiliaal?.adres, qrFiliaal?.stad].filter(Boolean).join(" - "),
                  regels: ["Scan om in te klokken"],
                });
                if (ok) toast.success("QR code gekopieerd — plak hem in je labelprogramma");
                else toast.error("Kopiëren lukt niet in deze browser, gebruik PNG downloaden");
              }}
              className="rounded-sm"
              data-testid="filiaal-qr-copy-btn"
            >
              <Copy size={16} weight="bold" className="mr-1.5" /> Kopieer
            </Button>
            <Button
              variant="outline"
              onClick={() => setQrDialogOpen(false)}
              className="rounded-sm"
              data-testid="close-qr-btn"
            >
              Sluiten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
